import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MapPin,
  ArrowRight,
  CheckCircle,
  MessageCircle,
  Clock,
  Shield,
  Zap,
} from 'lucide-react';
import { trackButtonClick } from '../lib/analytics';

const AboutPage = () => {
  const navigate = useNavigate();

  const handleStartProject = () => {
    trackButtonClick('Start Your Project', 'About');
    navigate('/contact');
  };

  const whoWeWorkWith = [
    {
      title: 'Small & Medium Businesses',
      description: 'Local businesses across the Isle of Man and UK looking to establish or improve their digital presence.',
    },
    {
      title: 'Service Providers',
      description: 'Professional services firms, consultants, and agencies who need systems that support their client work.',
    },
    {
      title: 'Growing Companies',
      description: 'Businesses that need scalable systems to support their expansion without constant rebuilding.',
    },
    {
      title: 'Non-Technical Founders',
      description: 'Business owners who know what they want to achieve but need technical expertise to get there.',
    },
  ];

  const systemsThinking = [
    {
      title: 'Connected, Not Isolated',
      description: 'We design solutions that work together. Your website, CRM, booking system, and marketing should all talk to each other.',
    },
    {
      title: 'Built for Reality',
      description: 'We understand that requirements change. Everything we build can be extended and adapted as your business evolves.',
    },
    {
      title: 'Automation First',
      description: 'If something can be automated reliably, it should be. We identify opportunities to save you time from day one.',
    },
    {
      title: 'Data as an Asset',
      description: 'Every system we build captures useful data that helps you make better decisions about your business.',
    },
  ];

  const processSteps = [
    {
      number: '01',
      title: 'Discovery & Planning',
      description: 'We start by understanding your business, goals, and constraints. No technical jargon — just clear conversation about what you\'re trying to achieve.',
      details: [
        'Initial consultation (video call or in-person)',
        'Understanding your business context',
        'Identifying requirements and priorities',
        'Exploring technical options',
        'Providing a clear proposal',
      ],
    },
    {
      number: '02',
      title: 'Design & Structure',
      description: 'Before writing any code, we design the solution and share it with you. You\'ll see exactly what you\'re getting before we build it.',
      details: [
        'Wireframes and visual mockups',
        'User journey mapping',
        'Technical architecture planning',
        'Content structure',
        'Your approval before we proceed',
      ],
    },
    {
      number: '03',
      title: 'Build & Integration',
      description: 'We develop your solution with regular check-ins so you can see progress. No disappearing acts — you\'ll know exactly where things stand.',
      details: [
        'Development in iterative stages',
        'Regular progress updates',
        'Preview links for you to review',
        'Integration with other systems',
        'Content population',
      ],
    },
    {
      number: '04',
      title: 'Testing & Refinement',
      description: 'We thoroughly test everything and make adjustments based on your feedback. Nothing launches until you\'re completely satisfied.',
      details: [
        'Cross-device testing',
        'Performance optimisation',
        'Security verification',
        'Your review and feedback',
        'Final adjustments',
      ],
    },
    {
      number: '05',
      title: 'Launch & Support',
      description: 'Go live with confidence. We handle the technical launch and remain available for questions, updates, and improvements.',
      details: [
        'Careful deployment',
        'Post-launch monitoring',
        'Training if needed',
        'Documentation handover',
        'Ongoing support options',
      ],
    },
  ];

  const clientExpectations = [
    {
      icon: MessageCircle,
      title: 'Clear Communication',
      description: 'We explain everything in plain English. You\'ll always understand what\'s happening and why.',
    },
    {
      icon: Clock,
      title: 'Realistic Timelines',
      description: 'We give honest estimates and keep you updated. If anything changes, you\'ll know immediately.',
    },
    {
      icon: Shield,
      title: 'Ownership & Control',
      description: 'Everything we build is yours. You get full access and aren\'t locked into ongoing relationships.',
    },
    {
      icon: Zap,
      title: 'Long-Term Thinking',
      description: 'We build for the future, not just today. Your systems will scale as your business grows.',
    },
  ];

  return (
    <div className="min-h-screen pt-24 lg:pt-28">
      <section className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="page-header animate-fade-in">
          <h1>How We Work</h1>
          <p>
            We're a digital systems company based in the Isle of Man, helping businesses across the UK build technology that actually works.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center mb-24 lg:mb-32">
          <div className="animate-fade-in-delay-1" style={{ opacity: 0 }}>
            <div className="flex items-center mb-5">
              <MapPin className="h-4 w-4 text-gray-500 mr-2" />
              <span className="text-gray-500 text-sm">Isle of Man based, UK-wide clients</span>
            </div>
            <h2 className="text-2xl lg:text-3xl font-semibold text-white mb-5">
              Real people, real accountability
            </h2>
            <p className="text-gray-500 mb-5 leading-relaxed">
              We're not a faceless agency or an offshore team. When you work with us, you're working with people who understand local business, speak your language, and are accountable for results.
            </p>
            <p className="text-gray-500 leading-relaxed">
              Our approach is straightforward: understand what you need, explain your options clearly, build something that works, and support you after launch.
            </p>
          </div>
          <div className="relative animate-fade-in-delay-2" style={{ opacity: 0 }}>
            <div className="aspect-[4/3] rounded-2xl overflow-hidden">
              <img
                src="/whatsapp_image_2026-01-11_at_13.20.52.jpeg"
                alt="Team collaboration"
                className="w-full h-full object-cover opacity-60"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="section-spacing-lg">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="section-header">
            <h2>Who We Work With</h2>
            <p>
              We work best with businesses who value quality and want a genuine partnership.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10">
            {whoWeWorkWith.map((item, index) => (
              <div key={index}>
                <h3 className="text-white font-medium mb-2 text-[15px]">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-spacing-lg">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <div>
              <h2 className="text-2xl lg:text-3xl font-semibold text-white mb-8">
                Systems Thinking, Not Just Websites
              </h2>
              <p className="text-gray-500 mb-10 leading-relaxed">
                Most web companies build websites. We build systems — interconnected tools that work together to help your business run more efficiently.
              </p>

              <div className="space-y-7">
                {systemsThinking.map((item, index) => (
                  <div key={index} className="flex items-start">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/15 flex items-center justify-center mt-1 mr-4">
                      <CheckCircle className="h-3 w-3 text-primary/80" />
                    </div>
                    <div>
                      <h3 className="text-white font-medium mb-1.5">{item.title}</h3>
                      <p className="text-gray-500 text-sm leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative hidden lg:block">
              <div className="aspect-square rounded-2xl overflow-hidden">
                <img
                  src="/whatsapp_image_2026-01-11_at_13.30.57.jpeg"
                  alt="Systems and data visualization"
                  className="w-full h-full object-cover opacity-60"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-spacing-lg">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="section-header">
            <h2>Our Process</h2>
            <p>
              Every project follows a clear, structured process designed to deliver results without surprises.
            </p>
          </div>

          <div className="space-y-5">
            {processSteps.map((step, index) => (
              <div key={index} className="card-premium p-7 lg:p-8">
                <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                  <div className="flex items-center lg:flex-col lg:items-center lg:w-20">
                    <div className="w-12 h-12 rounded-full bg-white/[0.03] border border-white/10 flex items-center justify-center">
                      <span className="text-gray-400 font-medium text-sm">{step.number}</span>
                    </div>
                  </div>

                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-white mb-2">{step.title}</h3>
                    <p className="text-gray-500 mb-5 leading-relaxed">{step.description}</p>

                    <ul className="grid sm:grid-cols-2 gap-2.5">
                      {step.details.map((detail, detailIndex) => (
                        <li key={detailIndex} className="flex items-center text-sm text-gray-500">
                          <CheckCircle className="h-3 w-3 text-primary/50 mr-2 flex-shrink-0" />
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-spacing-lg">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="section-header">
            <h2>What You Can Expect</h2>
            <p>
              Working with us means working with people who genuinely care about your success.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10 mb-16">
            {clientExpectations.map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-5">
                  <item.icon className="h-5 w-5 text-primary/70" />
                </div>
                <h3 className="text-white font-medium mb-2 text-[15px]">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>

          <div className="card-premium p-8 lg:p-10 text-center">
            <h2 className="text-xl font-semibold text-white mb-4">
              Ready to start your project?
            </h2>
            <p className="text-gray-500 mb-8 max-w-lg mx-auto leading-relaxed">
              Whether you know exactly what you need or just have an idea, let's have a conversation about how we can help.
            </p>
            <button onClick={handleStartProject} className="btn-primary">
              Start Your Project
              <ArrowRight className="ml-2 h-4 w-4 inline" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
